import type { ContractReportQueryDto } from "../../../autogen/bff-api";
import { base64Decode } from "../../../common/utils/base64";

export const CONTRACT_PAGE_SIZE = 10;

type Kind = "text" | "number" | "boolean" | "textList" | "objectList" | "record";

const FIELD_KINDS: Record<keyof ContractReportQueryDto, Kind> = {
  titleFilter: "text",
  templateIds: "textList",
  templateNameSearchTerm: "text",
  owningOrganizationId: "text",
  owningOrganizationIds: "textList",
  owningOrganizationName: "text",
  projectIds: "textList",
  projectSearchTerm: "text",
  internalPartyIds: "textList",
  internalPartySearchTerm: "text",
  visibleCustomFields: "record",
  visibleCustomFieldIds: "textList",
  startDateFrom: "text",
  startDateTo: "text",
  endDateFrom: "text",
  endDateTo: "text",
  hasNoEndDate: "boolean",
  createdAtFrom: "text",
  createdAtTo: "text",
  modifiedAtFrom: "text",
  modifiedAtTo: "text",
  publishedAtFrom: "text",
  publishedAtTo: "text",
  renewalDateFrom: "text",
  renewalDateTo: "text",
  terminationDateFrom: "text",
  terminationDateTo: "text",
  warrantyExpirationDateFrom: "text",
  warrantyExpirationDateTo: "text",
  datePresets: "record",
  internalOwnerIds: "textList",
  internalOwnerSearchTerm: "text",
  counterpartyIds: "textList",
  counterpartyName: "text",
  counterpartyContactFullName: "text",
  counterpartyContactFullNames: "textList",
  counterpartyContactEmail: "text",
  counterpartyContactEmails: "textList",
  counterpartyContactPhoneNumber: "text",
  counterpartyContactPhoneNumbers: "textList",
  statuses: "textList",
  state: "text",
  originSourcingEventIds: "textList",
  originSourcingEventName: "text",
  riskLevels: "textList",
  frameworkRoles: "textList",
  priorityLevels: "textList",
  supplierCategories: "textList",
  referenceNumberSearchTerm: "text",
  quickSearchTerm: "text",
  tableSearchTerm: "text",
  referenceNumbers: "textList",
  descriptionSearchTerm: "text",
  currencies: "textList",
  publishedByIds: "textList",
  publishedBySearchTerm: "text",
  terminatedByIds: "textList",
  terminatedBySearchTerm: "text",
  terminationReasons: "textList",
  linkedContractIds: "textList",
  linkedContractTitleSearchTerm: "text",
  noticePeriodFrom: "number",
  noticePeriodTo: "number",
  paymentTermsInDaysFrom: "number",
  paymentTermsInDaysTo: "number",
  pricePerMonthFrom: "number",
  pricePerMonthTo: "number",
  pricePerYearFrom: "number",
  pricePerYearTo: "number",
  totalLiabilityFrom: "number",
  totalLiabilityTo: "number",
  estimatedValueFrom: "number",
  estimatedValueTo: "number",
  totalPriceFrom: "number",
  totalPriceTo: "number",
  externalLinkSearchTerm: "text",
  externalLinks: "textList",
  purchasingPolicySearchTerm: "text",
  purchasingPolicies: "textList",
  documentIds: "textList",
  signedDocumentIds: "textList",
  industryIds: "textList",
  isClimateCommitted: "boolean",
  hasSignedCodeOfConduct: "boolean",
  hasCustomerContact: "boolean",
  customFieldSearch: "objectList",
  customFieldSelect: "objectList",
  customFieldValues: "objectList",
  optionNameSearchTerm: "text",
  optionNames: "textList",
  withoutActiveOwner: "boolean",
  missingRequiredData: "boolean",
  activeFrameworkAgreement: "boolean",
  withAnnualValueIn: "text",
  awaitingSignature: "boolean",
  signingDeadlinePassed: "boolean",
  signedFrom: "text",
  signedTo: "text",
  optionDeadlineFrom: "text",
  optionDeadlineTo: "text",
  optionDecisionDue: "boolean",
  optionDecisionWithoutDeadline: "boolean",
  limit: "number",
  skip: "number",
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const fits = (kind: Kind, value: unknown): boolean => {
  switch (kind) {
    case "text":
      return typeof value === "string";
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return typeof value === "boolean";
    case "textList":
      return Array.isArray(value) && value.every((item) => typeof item === "string");
    case "objectList":
      return Array.isArray(value) && value.every(isPlainObject);
    case "record":
      return isPlainObject(value);
  }
};

const isQueryField = (key: string): key is keyof ContractReportQueryDto => Object.hasOwn(FIELD_KINDS, key);

export const decodedContractQuery = (encoded: string | null): Partial<ContractReportQueryDto> | null => {
  if (!encoded) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(base64Decode(encoded));
  } catch {
    return null;
  }
  if (!isPlainObject(parsed)) return null;
  return Object.fromEntries(
    Object.entries(parsed).filter(([key, value]) => isQueryField(key) && fits(FIELD_KINDS[key], value))
  ) as Partial<ContractReportQueryDto>;
};

export const contractQueryFromAddress = (encoded: string | null, organizationId: string): ContractReportQueryDto => {
  const fromAddress = decodedContractQuery(encoded) ?? {};
  const skip = Math.max(0, Math.floor(fromAddress.skip ?? 0));
  return {
    ...fromAddress,
    owningOrganizationId: organizationId,
    limit: CONTRACT_PAGE_SIZE,
    skip: skip - (skip % CONTRACT_PAGE_SIZE),
  };
};
